import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import useAuth from '../../auth/useAuth';

import { navigation } from './data';

import {
  studentGeneralInfoQuery,
  consolidatedScoresQuery,
  tournamentsQuery,
} from './api';

import './styles.css';
import SubPagesLayout from '../UI/layout/subPages';
import AssignmentsList from '../UI/assignments';

const gameTypeToScoreKey = {
  'Account Classification': 'classification',
  'Normal Balance': 'normal_balance',
  'Balanced Equation': 'balanced_equation',
  'The Principles': 'principles'
};

function getBestScoresByGame(consolidatedScores) {
  if (!consolidatedScores) return {};

  const bestScores = {};

  for (const [gameType, scoreKey] of Object.entries(gameTypeToScoreKey)) {
    const sessions = consolidatedScores[scoreKey] || [];

    const bestByGame = {};
    for (const session of sessions) {
      const gameId = session.game;
      // Some game types score per-decision (total_points_session) and
      // some score per-level (total_level_points_session, e.g. the
      // classified-multistep level). Either field can be null when the
      // session uses only the other; sum both with a 0 fallback so the
      // displayed total reflects every points source for that session.
      const totalPoints = (session.session_summary?.total_points_session || 0)
        + (session.session_summary?.total_level_points_session || 0);
      const rank = session.session_summary?.rank || 'Noob';

      if (!bestByGame[gameId] || totalPoints > bestByGame[gameId].totalPoints) {
        bestByGame[gameId] = { totalPoints, rank };
      }
    }

    bestScores[gameType] = bestByGame;
  }

  return bestScores;
}

export default function Assignments() {
  const { getUser } = useAuth();

  const user = getUser();

  const { data: generalInfo } = useQuery(studentGeneralInfoQuery());
  const { data: consolidatedScores } = useQuery(consolidatedScoresQuery());
  const { data: allTournaments } = useQuery(tournamentsQuery());

  const games = generalInfo?.games;
  const sessions = generalInfo?.sessions;

  const tournamentGames = useMemo(() => {
    if (!games) return [];
    return games.filter(game => game.tournament_ids?.length > 0);
  }, [games]);

  const nonTournamentGames = useMemo(() => {
    if (!games) return [];
    return games.filter(game => !game.tournament_ids?.length);
  }, [games]);

  // Show one row per tournament (mirrors instructor page) instead of one
  // row per game. Filter to tournaments the student is actually part of.
  const tournaments = useMemo(() => {
    if (!allTournaments?.length || !tournamentGames.length) return [];
    const studentTournamentIds = new Set(
      tournamentGames.flatMap(game => game.tournament_ids || [])
    );
    return allTournaments.filter(tournament => studentTournamentIds.has(tournament.id));
  }, [allTournaments, tournamentGames]);

  const bestScores = useMemo(() => getBestScoresByGame(consolidatedScores), [consolidatedScores]);

  return (
    <SubPagesLayout
      navigation={navigation}
      contentClassName="assignments-page-wrapper"
    >
      <h1>Assignments</h1>
      <AssignmentsList
        user={user}
        games={games}
        nonTournamentGames={nonTournamentGames}
        tournamentGames={tournamentGames}
        tournaments={tournaments}
        sessions={sessions}
        bestScores={bestScores}
      />
    </SubPagesLayout>
  );
}
